import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { DraftContext } from '../../../../../../contexts/DraftContext'
import DraftUploader from '../../../../../FormComponents/DraftUploader'
import DraftUpload from '../../../../../../models/DraftUpload'
import DeleteUploadModal from './DeleteUploadModal'
import { useParams } from 'react-router-dom'
import { toastError, toastSuccess } from '../../../../../FormComponents/Toasts'
import { Archive, Trash3 } from 'react-bootstrap-icons'
import axiosService from '../../../../../../utils/AxiosService'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useReadOnly } from '../../../../../../contexts/ReadOnlyContext'

export default observer(function UploadSelectPanel({ action }) {
  const readOnly = useReadOnly()
  const { id } = useParams()
  const { draftState, selectedUpload, actions } = useContext(DraftContext)
  const [openDeleteUploadModal, setOpenDeleteUploadModal] = useState(false)

  const updateToArchive = () => {
    axiosService.patch(
      `/api/project_manager/campaigns/${id}/actions/${action.id}/draft_uploads/${selectedUpload.id}`,
      { draft: { is_archived: true } }
    )
    .then((updatedUpload) => {
      const draft = action.draft_uploads.find(upload => +upload.id === +updatedUpload.id)
      draft.isArchived = true
      toastSuccess('You have archived this upload!')
    })
    .catch(error => console.log(error))
  }

  const deleteUpload = () => {
    axiosService.delete(
      `/api/project_manager/campaigns/${id}/actions/${action.id}/draft_uploads/${selectedUpload.id}`
    )
    .then(() => {
      toastSuccess(`Upload deleted!`)
      action.draft_uploads = action.draft_uploads.filter(upload => +upload.id !== +selectedUpload.id)
      setOpenDeleteUploadModal(false)
    })
    .catch(error => console.log(error))
  }

  const onDragEnd = result => {
    if (!result.destination) return

    const { destination, source, draggableId } = result

    if (source.droppableId === destination.droppableId) {
      const newOrder = Array.from(action.draft_uploads)
      const [removed] = newOrder.splice(source.index, 1)
      newOrder.splice(destination.index, 0, removed)
      action.draft_uploads = newOrder
      axiosService.patch(
        `/api/project_manager/campaigns/${id}/actions/${action.id}/reorder_draft_uploads`,
        { draft_upload_order: newOrder.map(upload => upload.id) }
      )
      .then(response => {
        Object.assign(action, response)
      })
      .catch(error => {
        action.draft_uploads = Array.from(action.draft_uploads)
        console.error(error)
        toastError('Failed to update upload order')
      })
      return
    }

    const start = source.droppableId
    const finish = destination.droppableId

    if (start === finish) return
  }

  return (
    <div className='my-5 p-3 w-[650px] border-1 border-primary dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode rounded-lg overflow-hidden'>
      <div className='flex justify-between items-center'>
        <h3 className='mb-2 pt-1 font-bold text-md dark:text-white'>{action.draft_uploads.length > 0 ? selectedUpload.fileName : 'No uploads'}</h3>
        {!readOnly && <div className='flex gap-2'>
          <button
            className={`flex items-center rounded-lg p-2 dark:text-white ${action.draftAccepted ? 'opacity-30' : 'hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter'}`}
            disabled={action.draftAccepted}
            onClick={() => updateToArchive()}
          >
            <Archive />
            <span className='ml-1 text-sm font-bold'>Archive Upload</span>
          </button>
          <button 
            className={`flex items-center rounded-lg p-2 dark:text-white ${action.draftAccepted ? 'opacity-30' : 'hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter'}`}
            disabled={action.draftAccepted}
            onClick={() => setOpenDeleteUploadModal(true)}
          >
            <Trash3 />
            <span className='ml-1 text-sm font-bold'>Delete Upload</span>
          </button>
        </div>}
      </div>
      <div className='flex items-center justify-start gap-4 min-w-full overflow-x-scroll'>
        <DragDropContext onDragEnd={onDragEnd} >
          <Droppable droppableId="uploads" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='flex items-center justify-start gap-4 min-w-full overflow-x-scroll'
              >
                {
                  action?.draft_uploads && action.draft_uploads.length > 0 ? action.draft_uploads.map((upload, index) => (
                    <Draggable key={`upload-${upload.id}`} draggableId={`upload-${upload.id}`} index={index} isDragDisabled={readOnly}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={() => {
                            draftState.commentable = false
                            draftState.selectedUpload = upload
                          }}
                          className='relative cursor-pointer select-none'
                          style={{ ...provided.draggableProps.style }}
                        >
                          {(upload.videoThumb || upload.url) ? (
                            <img
                              key={upload.url || upload.id}
                              src={upload.videoThumb || upload.url}
                              alt={upload.fileName}
                              className={`${selectedUpload.id === upload.id ? 'border-2 border-rb-green' : ''} h-12 min-w-12 max-w-12 rounded-lg object-cover`}
                            />
                          ) : (
                            <div className="h-12 min-w-12 max-w-12 rounded-lg bg-gray-100 dark:bg-rb-dark-mode-lighter animate-pulse" />
                          )}
                          {
                            upload.isArchived && (
                              <div className='absolute top-0 right-0 min-h-12 min-w-12 max-w-12 rounded-lg bg-black opacity-60 flex justify-center items-center'><Archive className='text-white'/></div>
                            )
                          }
                        </div>

                      )}
                    </Draggable>
                  )) : <div className="dark:text-white">Uploads will appear here</div>
                }
                {
                  !readOnly && !action.draftAccepted && (
                    <div className='min-h-12 min-w-12 flex-shrink-0'>
                      <DraftUploader
                        isCompact
                      url={`/api/project_manager/campaigns/${id}/actions/${action.id}/draft_uploads`}
                      onDocumentUpload={response => {
                        response.forEach(draft => {
                          action.draft_uploads.push(new DraftUpload(draft))
                        })
                        const fileCount = response.length
                        toastSuccess(`${fileCount} ${fileCount === 1 ? 'upload' : 'uploads'} added!`)
                      }}
                      onDocumentUploadFailure={({ data: { error } }) => toastError(error)}
                      />
                    </div>
                  )
                }
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <DeleteUploadModal
        isOpen={openDeleteUploadModal}
        onClose={() => setOpenDeleteUploadModal(false)}
        onDelete={deleteUpload}
        uploadName={selectedUpload.fileName}
      />
    </div>
  )
})
